import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FileUploader } from 'ng2-file-upload';
import { ToastrService } from 'ngx-toastr';
import { ConversionService } from '../conversion.service';
import {
	ConversionAtoBFundingResult,
	ConversionAtoBWalletFunding,
	ConversionClientIdentifier,
	ConversionTermsLookup,
	ConversionClientDebtorRelationship,
	ConversionClientDebtorTie,
	ConversionWinFactorClientNote,
	ConversionWinFactorInvoiceNote,
	ConversionWinFactorCustomerNote,
	ConversionCustomerNoteCustomer,
	ConversionCustomerNoteSendResult,
	ConversionWinFactorFundingOption,
	ConversionDataUploadResult,
	ConversionDataUploadSummary,
	ConversionDocument,
	ConversionDocumentClient,
	ConversionDocumentSendResult,
	ConversionWinFactorClientName,
} from '../models/ConversionDataUpload';
import { ConsoleLogger } from '../../../core/utilities/console-logger';
import { downloadCsv } from '../skipped-invoices-csv';

/**
 * Conversion > Data Upload. Loads the data the conversion needs from outside Aladdin:
 * - the AtoB digital wallet report (CSV) into FactorCloud.AtoBWallet;
 * - then an "AtoB Wallet" DIGITAL WALLET funding instruction for each wallet, on the FactorCloud
 *   client of the active WinFactor client whose company name matches the wallet's company name;
 * - the WinFactor client documents in blob storage (the WinFactor import container,
 *   conversion-documents folder) into WinFactor.Documents;
 * - then those documents, per client or in bulk, into FactorCloud (POST /documents) on the
 *   carrier's FactorCloud client.
 */
@Component({
	selector: 'app-conversion-data-upload',
	templateUrl: './conversion-data-upload.component.html',
	styleUrls: ['./conversion-data-upload.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class ConversionDataUploadComponent implements OnInit {
	public summary: ConversionDataUploadSummary = null;
	public loadingSummary = false;

	// AtoB wallet report
	public atoBUploader: FileUploader;
	public fileDragged = false;
	public atoBFile: File = null;
	public previewColumns: any[] = [];
	public previewRows: any[] = [];
	public previewTotalRows = 0;
	public uploadingAtoB = false;
	public atoBResult: ConversionDataUploadResult = null;

	// AtoB wallet funding instructions
	public atoBWallets: ConversionAtoBWalletFunding[] = [];
	public loadingAtoBWallets = false;
	public atoBFilter = '';
	public atoBMatchFilter = '';
	public readonly atoBMatchStatuses = ['Matched', 'NotConverted', 'Ambiguous', 'Sent'];
	/** 'wallets' = matched wallets; 'unmatched' = active WinFactor clients without a wallet. */
	public atoBTab = 'wallets';
	public unmatchedClients: ConversionWinFactorClientName[] = [];
	public loadingUnmatchedClients = false;
	public unmatchedFilter = '';
	public sendingAtoB = false;
	public atoBSendDone = 0;
	public atoBSendTotal = 0;
	public atoBSendSent = 0;
	public atoBSendSkipped = 0;
	public atoBSendFailures: ConversionAtoBFundingResult[] = [];

	// WinFactor client identifiers (uses the same file picker and preview as the AtoB report)
	public uploadingIdentifiers = false;
	public identifiersResult: ConversionDataUploadResult = null;
	public clientIdentifiers: ConversionClientIdentifier[] = [];
	public loadingClientIdentifiers = false;
	public identifierFilter = '';

	// Transfer terms file (FactorCloud.TermsLookup); same file picker, .xlsx / .xls / .csv
	public uploadingTerms = false;
	public termsResult: ConversionDataUploadResult = null;
	public termsLookups: ConversionTermsLookup[] = [];
	public loadingTermsLookups = false;
	public termsFilter = '';
	public termsOverridesOnly = false;

	// WinFactor Funding Options file (secure.WFBD); same file picker, .xlsx / .xls / .csv
	public uploadingFunding = false;
	public fundingResult: ConversionDataUploadResult = null;
	public fundingOptions: ConversionWinFactorFundingOption[] = [];
	public loadingFundingOptions = false;
	public fundingFilter = '';

	// WinFactor client debtor relationships (WinFactor.ClientDebtorRelationships); ~250,000 rows, searched on the server
	public uploadingRelationships = false;
	public relationshipsResult: ConversionDataUploadResult = null;
	public relationships: ConversionClientDebtorRelationship[] = [];
	public loadingRelationships = false;
	public relationshipSearch = '';
	public readonly relationshipRowLimit = 500;

	// WinFactor "Active Clients with Customer Info" report (WinFactor.ClientDebtorTies); ~250,000 rows, searched on the server
	public uploadingTies = false;
	public tiesResult: ConversionDataUploadResult = null;
	public ties: ConversionClientDebtorTie[] = [];
	public loadingTies = false;
	public tieSearch = '';

	// WinFactor client notes (WinFactor.ClientNotes) and invoice notes (WinFactor.InvoiceNotes), searched on the server
	public uploadingNotes = false;
	public notesResult: ConversionDataUploadResult = null;
	public clientNotes: ConversionWinFactorClientNote[] = [];
	public invoiceNotes: ConversionWinFactorInvoiceNote[] = [];
	public loadingNotes = false;
	public noteSearch = '';

	// WinFactor customer notes (WinFactor.CustomerNotes), sent to each customer's FactorCloud debtor as General Notes
	public customerNotes: ConversionWinFactorCustomerNote[] = [];
	public noteCustomers: ConversionCustomerNoteCustomer[] = [];
	public loadingNoteCustomers = false;
	public noteCustomerFilter = '';
	public noteCustomerStatus = 'all';
	public readonly noteCustomerRowLimit = 500;
	public selectedNoteCustomers: { [customerId: number]: boolean } = {};
	public noteCustomerResults: { [customerId: number]: ConversionCustomerNoteSendResult } = {};
	public sendingCustomerNotes = false;
	public customerNoteSendDone = 0;
	public customerNoteSendTotal = 0;
	public customerNoteSendSent = 0;
	public customerNoteSendAlreadySent = 0;
	public customerNoteSendLeftAlone = 0;
	public customerNoteSendFailures: ConversionCustomerNoteSendResult[] = [];
	private stopCustomerNoteSend = false;

	// Client / customer documents
	public importingDocuments = false;
	public documentsResult: ConversionDataUploadResult = null;

	// Send documents to FactorCloud
	public documentClients: ConversionDocumentClient[] = [];
	public loadingDocumentClients = false;
	public clientFilter = '';
	public currentClient: ConversionDocumentClient = null;
	public clientDocuments: ConversionDocument[] = [];
	public loadingClientDocuments = false;
	public sending = false;
	public sendDone = 0;
	public sendTotal = 0;
	public sendSent = 0;
	public sendSkipped = 0;
	public sendFailures: ConversionDocumentSendResult[] = [];

	private readonly previewRowLimit = 25;

	/**
	 * Which page this is (route data 'section'): 'atob' = Dev Tools > Conversion > AtoB Wallets,
	 * 'identifiers' = Dev Tools > Conversion > Client Identifiers, 'terms' = Dev Tools > Conversion >
	 * Terms Lookup, 'clientdocuments' = Client Documents, 'customerdocuments' = Customer Documents. One component serves
	 * them all so the upload and send logic is not duplicated; each page shows and loads only its own sections.
	 */
	public section = 'atob';

	constructor(
		private conversionService: ConversionService,
		private toastrService: ToastrService,
		private route: ActivatedRoute
	) {}

	ngOnInit() {
		const data = this.route.snapshot.data;
		this.section =
			data &&
			(data.section === 'clientdocuments' ||
				data.section === 'customerdocuments' ||
				data.section === 'identifiers' ||
				data.section === 'terms' ||
				data.section === 'funding' ||
				data.section === 'relationships' ||
				data.section === 'ties' ||
				data.section === 'clientnotes' ||
				data.section === 'invoicenotes' ||
				data.section === 'customernotes')
				? data.section
				: 'atob';

		this.atoBUploader = new FileUploader({
			url: '',
			disableMultipart: true,
			filters: [
				{
					name: 'acceptedFileTypes',
					fn: (item: any) => !!item && !!item.name && this.isAcceptedFile(item.name),
				},
			],
		});
		this.atoBUploader.onWhenAddingFileFailed = () => {
			this.toastrService.warning(
				this.acceptsExcel ? 'Only .xlsx, .xls or .csv files are accepted.' : 'Only .csv files are accepted.',
				this.uploadTitle
			);
		};
		// Fires for a dropped file and for one picked in the file dialog alike.
		this.atoBUploader.onAfterAddingFile = () => {
			setTimeout(() => this.fileChosen());
		};
		this.loadSummary();
		if (this.section === 'atob') {
			this.loadAtoBWallets();
		} else if (this.section === 'identifiers') {
			this.loadClientIdentifiers();
		} else if (this.section === 'terms') {
			this.loadTermsLookups();
		} else if (this.section === 'funding') {
			this.loadFundingOptions();
		} else if (this.section === 'relationships') {
			this.loadRelationships();
		} else if (this.section === 'ties') {
			this.loadTies();
		} else if (this.section === 'clientnotes' || this.section === 'invoicenotes') {
			this.loadNotes();
		} else if (this.section === 'customernotes') {
			this.loadNotes();
			this.loadNoteCustomers();
		} else {
			this.loadDocumentClients();
		}
	}

	get pageTitle(): string {
		return this.section === 'atob'
			? 'AtoB Wallets'
			: this.section === 'identifiers'
				? 'Client Identifiers'
				: this.section === 'terms'
					? 'Terms Lookup'
					: this.section === 'funding'
						? 'Funding Options'
						: this.section === 'relationships'
							? 'Client Debtor Relationships'
							: this.section === 'ties'
								? 'Client Debtor Ties'
								: this.section === 'clientnotes'
									? 'Client Notes'
									: this.section === 'invoicenotes'
										? 'Invoice Notes'
										: this.section === 'customernotes'
											? 'Customer Notes'
											: this.documentsTitle;
	}

	/** The Customer Documents page (customer folders, sent to FactorCloud debtors); otherwise Client Documents. */
	get customerDocuments(): boolean {
		return this.section === 'customerdocuments';
	}

	get isDocumentsSection(): boolean {
		return this.section === 'clientdocuments' || this.section === 'customerdocuments';
	}

	get documentsTitle(): string {
		return this.customerDocuments ? 'Customer Documents' : 'Client Documents';
	}

	get uploadTitle(): string {
		return this.section === 'identifiers'
			? 'Client Identifiers'
			: this.section === 'terms'
				? 'Transfer Terms'
				: this.section === 'funding'
					? 'Funding Options'
					: this.section === 'relationships'
						? 'Client Debtor Relationships'
						: this.section === 'ties'
							? 'Client Debtor Ties'
							: this.section === 'clientnotes'
								? 'Client Notes'
								: this.section === 'invoicenotes'
									? 'Invoice Notes'
									: this.section === 'customernotes'
										? 'Customer Notes'
										: 'AtoB Wallet Report';
	}

	/** The Terms Lookup page also takes Excel files; the other uploads are .csv only. */
	private isAcceptedFile(name: string): boolean {
		const lower = name.toLowerCase();
		if (lower.endsWith('.csv')) {
			return true;
		}
		return this.acceptsExcel && (lower.endsWith('.xlsx') || lower.endsWith('.xls'));
	}

	/** The pages that take Excel files as well as .csv. */
	get acceptsExcel(): boolean {
		return (
			this.section === 'terms' ||
			this.section === 'funding' ||
			this.section === 'relationships' ||
			this.section === 'ties' ||
			this.section === 'clientnotes' ||
			this.section === 'invoicenotes' ||
			this.section === 'customernotes'
		);
	}

	loadSummary() {
		this.loadingSummary = true;
		this.conversionService.getDataUploadSummary().subscribe(
			(summary) => {
				this.summary = summary;
				this.loadingSummary = false;
			},
			(error) => {
				ConsoleLogger.logInfo('conversion data upload summary error', error);
				this.loadingSummary = false;
			}
		);
	}

	onDragover() {
		this.fileDragged = true;
	}

	onDragout() {
		this.fileDragged = false;
	}

	/** Called after a file is dropped or picked: keeps the newest one and previews it. */
	fileChosen() {
		this.fileDragged = false;
		const queue = this.atoBUploader.queue;
		if (!queue || queue.length === 0) {
			return;
		}

		const item = queue[queue.length - 1];
		this.atoBUploader.clearQueue();
		this.atoBFile = item._file;
		this.atoBResult = null;
		this.previewColumns = [];
		this.previewRows = [];
		this.previewTotalRows = 0;

		// Excel files are read on the server; only a .csv is previewed here.
		if (!this.atoBFile.name.toLowerCase().endsWith('.csv')) {
			return;
		}

		const reader = new FileReader();
		reader.onload = () => this.buildPreview(reader.result ? reader.result.toString() : '');
		reader.readAsText(this.atoBFile);
	}

	resetAtoB() {
		this.atoBUploader.clearQueue();
		this.atoBFile = null;
		this.previewColumns = [];
		this.previewRows = [];
		this.previewTotalRows = 0;
	}

	uploadAtoB() {
		if (!this.atoBFile || this.uploadingAtoB) {
			return;
		}

		this.uploadingAtoB = true;
		this.atoBResult = null;
		this.conversionService.uploadAtoBWalletReport(this.atoBFile).subscribe(
			(result) => {
				this.uploadingAtoB = false;
				this.atoBResult = result;
				this.toastrService.success(result.summary, 'AtoB Wallet Report');
				this.resetAtoB();
				this.loadSummary();
				this.loadAtoBWallets();
			},
			(error) => {
				this.uploadingAtoB = false;
				this.toastrService.error(this.errorText(error), 'AtoB Wallet Report');
			}
		);
	}

	uploadIdentifiers() {
		if (!this.atoBFile || this.uploadingIdentifiers) {
			return;
		}

		this.uploadingIdentifiers = true;
		this.identifiersResult = null;
		this.conversionService.uploadClientIdentifiers(this.atoBFile).subscribe(
			(result) => {
				this.uploadingIdentifiers = false;
				this.identifiersResult = result;
				this.toastrService.success(result.summary, 'Client Identifiers');
				this.resetAtoB();
				this.loadSummary();
				this.loadClientIdentifiers();
			},
			(error) => {
				this.uploadingIdentifiers = false;
				this.toastrService.error(this.errorText(error), 'Client Identifiers');
			}
		);
	}

	loadClientIdentifiers() {
		this.loadingClientIdentifiers = true;
		this.conversionService.getClientIdentifiers().subscribe(
			(identifiers) => {
				this.clientIdentifiers = identifiers || [];
				this.loadingClientIdentifiers = false;
			},
			(error) => {
				this.loadingClientIdentifiers = false;
				this.toastrService.error(this.errorText(error), 'Client Identifiers');
			}
		);
	}

	uploadTerms() {
		if (!this.atoBFile || this.uploadingTerms) {
			return;
		}

		this.uploadingTerms = true;
		this.termsResult = null;
		this.conversionService.uploadTermsLookup(this.atoBFile).subscribe(
			(result) => {
				this.uploadingTerms = false;
				this.termsResult = result;
				this.toastrService.success(result.summary, 'Transfer Terms');
				this.resetAtoB();
				this.loadSummary();
				this.loadTermsLookups();
			},
			(error) => {
				this.uploadingTerms = false;
				this.toastrService.error(this.errorText(error), 'Transfer Terms');
			}
		);
	}

	loadTermsLookups() {
		this.loadingTermsLookups = true;
		this.conversionService.getTermsLookups().subscribe(
			(lookups) => {
				this.termsLookups = lookups || [];
				this.loadingTermsLookups = false;
			},
			(error) => {
				this.loadingTermsLookups = false;
				this.toastrService.error(this.errorText(error), 'Transfer Terms');
			}
		);
	}

	uploadRelationships() {
		if (!this.atoBFile || this.uploadingRelationships) {
			return;
		}

		this.uploadingRelationships = true;
		this.relationshipsResult = null;
		this.conversionService.uploadClientDebtorRelationships(this.atoBFile).subscribe(
			(result) => {
				this.uploadingRelationships = false;
				this.relationshipsResult = result;
				this.toastrService.success(result.summary, 'Client Debtor Relationships');
				this.resetAtoB();
				this.loadSummary();
				this.loadRelationships();
			},
			(error) => {
				this.uploadingRelationships = false;
				this.toastrService.error(this.errorText(error), 'Client Debtor Relationships');
			}
		);
	}

	loadRelationships() {
		this.loadingRelationships = true;
		this.conversionService.getClientDebtorRelationships(this.relationshipSearch).subscribe(
			(relationships) => {
				this.relationships = relationships || [];
				this.loadingRelationships = false;
			},
			(error) => {
				this.loadingRelationships = false;
				this.toastrService.error(this.errorText(error), 'Client Debtor Relationships');
			}
		);
	}

	uploadTies() {
		if (!this.atoBFile || this.uploadingTies) {
			return;
		}

		this.uploadingTies = true;
		this.tiesResult = null;
		this.conversionService.uploadClientDebtorTies(this.atoBFile).subscribe(
			(result) => {
				this.uploadingTies = false;
				this.tiesResult = result;
				this.toastrService.success(result.summary, 'Client Debtor Ties');
				this.resetAtoB();
				this.loadSummary();
				this.loadTies();
			},
			(error) => {
				this.uploadingTies = false;
				this.toastrService.error(this.errorText(error), 'Client Debtor Ties');
			}
		);
	}

	loadTies() {
		this.loadingTies = true;
		this.conversionService.getClientDebtorTies(this.tieSearch).subscribe(
			(ties) => {
				this.ties = ties || [];
				this.loadingTies = false;
			},
			(error) => {
				this.loadingTies = false;
				this.toastrService.error(this.errorText(error), 'Client Debtor Ties');
			}
		);
	}

	/** Uploads the client, invoice or customer notes file, whichever page this is. */
	uploadNotes() {
		if (!this.atoBFile || this.uploadingNotes) {
			return;
		}

		const title = this.uploadTitle;
		const upload =
			this.section === 'clientnotes'
				? this.conversionService.uploadClientNotes(this.atoBFile)
				: this.section === 'customernotes'
					? this.conversionService.uploadCustomerNotes(this.atoBFile)
					: this.conversionService.uploadInvoiceNotes(this.atoBFile);
		this.uploadingNotes = true;
		this.notesResult = null;
		upload.subscribe(
			(result) => {
				this.uploadingNotes = false;
				this.notesResult = result;
				this.toastrService.success(result.summary, title);
				this.resetAtoB();
				this.loadSummary();
				this.loadNotes();
				if (this.section === 'customernotes') {
					this.loadNoteCustomers();
				}
			},
			(error) => {
				this.uploadingNotes = false;
				this.toastrService.error(this.errorText(error), title);
			}
		);
	}

	/** Loads the saved client, invoice or customer notes matching the search, whichever page this is. */
	loadNotes() {
		this.loadingNotes = true;
		if (this.section === 'customernotes') {
			this.conversionService.getCustomerNotes(this.noteSearch).subscribe(
				(notes) => {
					this.customerNotes = notes || [];
					this.loadingNotes = false;
				},
				(error) => {
					this.loadingNotes = false;
					this.toastrService.error(this.errorText(error), 'Customer Notes');
				}
			);
		} else if (this.section === 'clientnotes') {
			this.conversionService.getClientNotes(this.noteSearch).subscribe(
				(notes) => {
					this.clientNotes = notes || [];
					this.loadingNotes = false;
				},
				(error) => {
					this.loadingNotes = false;
					this.toastrService.error(this.errorText(error), 'Client Notes');
				}
			);
		} else {
			this.conversionService.getInvoiceNotes(this.noteSearch).subscribe(
				(notes) => {
					this.invoiceNotes = notes || [];
					this.loadingNotes = false;
				},
				(error) => {
					this.loadingNotes = false;
					this.toastrService.error(this.errorText(error), 'Invoice Notes');
				}
			);
		}
	}

	/** Loads every customer with saved notes, with its FactorCloud debtor and how many notes were sent. */
	loadNoteCustomers() {
		this.loadingNoteCustomers = true;
		this.conversionService.getCustomerNoteCustomers('').subscribe(
			(customers) => {
				this.noteCustomers = customers || [];
				this.loadingNoteCustomers = false;
			},
			(error) => {
				this.loadingNoteCustomers = false;
				this.toastrService.error(this.errorText(error), 'Customer Notes');
			}
		);
	}

	/** Where a customer's notes stand: Sent, Partly sent (new notes since), Ready, or No FactorCloud debtor / No Aladdin debtor. */
	noteCustomerState(customer: ConversionCustomerNoteCustomer): string {
		if (customer.sentNoteCount > 0 && customer.sentNoteCount >= customer.noteCount) {
			return 'Sent';
		}
		if (!customer.debtorKey) {
			return 'No Aladdin debtor';
		}
		if (!customer.factorCloudDebtorId) {
			return 'No FactorCloud debtor';
		}
		return customer.sentNoteCount > 0 ? 'New notes' : 'Ready';
	}

	/** A customer whose notes can be sent now: in FactorCloud, with notes not yet sent. */
	isNoteCustomerReady(customer: ConversionCustomerNoteCustomer): boolean {
		return !!customer.factorCloudDebtorId && customer.sentNoteCount < customer.noteCount;
	}

	/** The customers matching the filter box (ID, WinFactor or Aladdin name, FactorCloud id) and the status filter. */
	get filteredNoteCustomers(): ConversionCustomerNoteCustomer[] {
		const text = (this.noteCustomerFilter || '').trim().toLowerCase();
		return this.noteCustomers.filter((x) => {
			const state = this.noteCustomerState(x);
			if (this.noteCustomerStatus === 'ready' && !this.isNoteCustomerReady(x)) {
				return false;
			}
			if (this.noteCustomerStatus === 'sent' && state !== 'Sent') {
				return false;
			}
			if (this.noteCustomerStatus === 'notinfc' && state !== 'No Aladdin debtor' && state !== 'No FactorCloud debtor') {
				return false;
			}
			if (!text) {
				return true;
			}
			return (
				String(x.customerId) === text ||
				(x.customerName || '').toLowerCase().indexOf(text) >= 0 ||
				(x.debtorName || '').toLowerCase().indexOf(text) >= 0 ||
				(x.factorCloudDebtorId || '').toLowerCase() === text
			);
		});
	}

	/** The filtered customers shown (the first noteCustomerRowLimit). */
	get shownNoteCustomers(): ConversionCustomerNoteCustomer[] {
		return this.filteredNoteCustomers.slice(0, this.noteCustomerRowLimit);
	}

	get noteCustomerReadyCount(): number {
		return this.noteCustomers.filter((x) => this.isNoteCustomerReady(x)).length;
	}

	get selectedNoteCustomerIds(): number[] {
		return this.noteCustomers.filter((x) => this.selectedNoteCustomers[x.customerId]).map((x) => x.customerId);
	}

	get allShownReadyNoteCustomersSelected(): boolean {
		const ready = this.shownNoteCustomers.filter((x) => this.isNoteCustomerReady(x));
		return ready.length > 0 && ready.every((x) => !!this.selectedNoteCustomers[x.customerId]);
	}

	toggleShownReadyNoteCustomers(checked: boolean) {
		this.shownNoteCustomers.filter((x) => this.isNoteCustomerReady(x)).forEach((x) => (this.selectedNoteCustomers[x.customerId] = checked));
	}

	/** Sends the ticked customers' notes. */
	sendSelectedCustomerNotes() {
		const ids = this.selectedNoteCustomerIds;
		if (ids.length === 0 || this.sendingCustomerNotes) {
			return;
		}
		this.startSendingCustomerNotes(ids);
	}

	/** Sends the notes of every customer that is in FactorCloud and has notes not yet sent. */
	sendAllReadyCustomerNotes() {
		if (this.sendingCustomerNotes) {
			return;
		}
		const ids = this.noteCustomers.filter((x) => this.isNoteCustomerReady(x)).map((x) => x.customerId);
		if (ids.length === 0) {
			this.toastrService.info('Nothing to send: no customer in FactorCloud has notes still to send.', 'Customer Notes');
			return;
		}
		this.startSendingCustomerNotes(ids);
	}

	/** Stops a send after the customer in flight. */
	stopSendingCustomerNotes() {
		this.stopCustomerNoteSend = true;
	}

	/** One customer per request, so a long batch shows progress and never hits a request timeout. */
	private startSendingCustomerNotes(customerIds: number[]) {
		this.sendingCustomerNotes = true;
		this.stopCustomerNoteSend = false;
		this.customerNoteSendDone = 0;
		this.customerNoteSendTotal = customerIds.length;
		this.customerNoteSendSent = 0;
		this.customerNoteSendAlreadySent = 0;
		this.customerNoteSendLeftAlone = 0;
		this.customerNoteSendFailures = [];

		const next = (index: number) => {
			if (index >= customerIds.length || this.stopCustomerNoteSend) {
				this.finishSendingCustomerNotes();
				return;
			}
			this.conversionService.sendCustomerNotes([customerIds[index]]).subscribe(
				(results) => {
					(results || []).forEach((result) => this.recordCustomerNoteSendResult(result));
					this.customerNoteSendDone++;
					next(index + 1);
				},
				(error) => {
					this.recordCustomerNoteSendResult({
						customerId: customerIds[index],
						customerName: null,
						factorCloudDebtorId: null,
						status: 'Failed',
						message: this.errorText(error),
					});
					this.customerNoteSendDone++;
					next(index + 1);
				}
			);
		};
		next(0);
	}

	private recordCustomerNoteSendResult(result: ConversionCustomerNoteSendResult) {
		if (result.status === 'Sent') {
			this.customerNoteSendSent++;
		} else if (result.status === 'AlreadySent') {
			this.customerNoteSendAlreadySent++;
		} else if (result.status === 'Skipped') {
			this.customerNoteSendLeftAlone++;
		} else {
			this.customerNoteSendFailures.push(result);
		}
		this.noteCustomerResults[result.customerId] = result;
		this.selectedNoteCustomers[result.customerId] = false;
	}

	private finishSendingCustomerNotes() {
		this.sendingCustomerNotes = false;
		const failed = this.customerNoteSendFailures.length;
		const text =
			`${this.customerNoteSendSent} customer(s)' notes sent to FactorCloud` +
			(this.customerNoteSendAlreadySent > 0 ? `, ${this.customerNoteSendAlreadySent} already there` : '') +
			(this.customerNoteSendLeftAlone > 0 ? `, ${this.customerNoteSendLeftAlone} left alone (FactorCloud already had General Notes)` : '') +
			(failed > 0 ? `, ${failed} not sent.` : '.') +
			(this.stopCustomerNoteSend ? ' Stopped before the end.' : '');
		if (failed > 0) {
			this.toastrService.warning(text, 'Customer Notes');
		} else {
			this.toastrService.success(text, 'Customer Notes');
		}
		this.loadSummary();
		this.loadNoteCustomers();
		this.loadNotes();
	}

	/** Where a tie stands for the conversion: linked (has an Aladdin debtor), or why it cannot be linked. */
	tieStatus(tie: ConversionClientDebtorTie): string {
		if (tie.debtorKey) {
			return 'Will link';
		}
		return tie.winFactorCustomerId ? 'No Aladdin debtor' : 'No WinFactor customer';
	}

	uploadFunding() {
		if (!this.atoBFile || this.uploadingFunding) {
			return;
		}

		this.uploadingFunding = true;
		this.fundingResult = null;
		this.conversionService.uploadFundingOptions(this.atoBFile).subscribe(
			(result) => {
				this.uploadingFunding = false;
				this.fundingResult = result;
				this.toastrService.success(result.summary, 'Funding Options');
				this.resetAtoB();
				this.loadSummary();
				this.loadFundingOptions();
			},
			(error) => {
				this.uploadingFunding = false;
				this.toastrService.error(this.errorText(error), 'Funding Options');
			}
		);
	}

	loadFundingOptions() {
		this.loadingFundingOptions = true;
		this.conversionService.getFundingOptions().subscribe(
			(options) => {
				this.fundingOptions = options || [];
				this.loadingFundingOptions = false;
			},
			(error) => {
				this.loadingFundingOptions = false;
				this.toastrService.error(this.errorText(error), 'Funding Options');
			}
		);
	}

	get filteredFundingOptions(): ConversionWinFactorFundingOption[] {
		const needle = (this.fundingFilter || '').trim().toLowerCase();
		if (!needle) {
			return this.fundingOptions;
		}
		return this.fundingOptions.filter(
			(x) =>
				x.winFactorClientId.toString().indexOf(needle) >= 0 ||
				(x.clientName || '').toLowerCase().indexOf(needle) >= 0 ||
				(x.fundingType || '').toLowerCase().indexOf(needle) >= 0 ||
				(x.bankName || '').toLowerCase().indexOf(needle) >= 0 ||
				(x.accountName || '').toLowerCase().indexOf(needle) >= 0
		);
	}

	/** True for a term other than the default NEW SYSTEM TRANSFER. */
	isTermOverride(lookup: ConversionTermsLookup): boolean {
		return (lookup.transferTermName || '').trim().toUpperCase() !== 'NEW SYSTEM TRANSFER';
	}

	get termsOverrideCount(): number {
		return this.termsLookups.filter((x) => this.isTermOverride(x)).length;
	}

	get filteredTermsLookups(): ConversionTermsLookup[] {
		const needle = (this.termsFilter || '').trim().toLowerCase();
		return this.termsLookups.filter(
			(x) =>
				(!this.termsOverridesOnly || this.isTermOverride(x)) &&
				(!needle ||
					x.winFactorClientId.toString().indexOf(needle) >= 0 ||
					(x.companyName || '').toLowerCase().indexOf(needle) >= 0 ||
					(x.transferTermName || '').toLowerCase().indexOf(needle) >= 0 ||
					(x.liveTermName || '').toLowerCase().indexOf(needle) >= 0)
		);
	}

	get filteredClientIdentifiers(): ConversionClientIdentifier[] {
		const needle = (this.identifierFilter || '').trim().toLowerCase();
		if (!needle) {
			return this.clientIdentifiers;
		}
		return this.clientIdentifiers.filter(
			(x) =>
				x.winFactorClientId.toString().indexOf(needle) >= 0 ||
				(x.clientName || '').toLowerCase().indexOf(needle) >= 0 ||
				(x.clientEin || '').toLowerCase().indexOf(needle) >= 0 ||
				(x.clientFileNumber || '').toLowerCase().indexOf(needle) >= 0 ||
				(x.mcNumber || '').toLowerCase().indexOf(needle) >= 0 ||
				(x.usDotNumber || '').toLowerCase().indexOf(needle) >= 0
		);
	}

	importDocuments() {
		if (this.importingDocuments) {
			return;
		}

		this.importingDocuments = true;
		this.documentsResult = null;
		this.conversionService.importDocuments(this.customerDocuments).subscribe(
			(result) => {
				this.importingDocuments = false;
				this.documentsResult = result;
				this.toastrService.success(result.summary, this.documentsTitle);
				this.loadSummary();
				this.loadDocumentClients();
			},
			(error) => {
				this.importingDocuments = false;
				this.toastrService.error(this.errorText(error), this.documentsTitle);
			}
		);
	}

	// ---------------- AtoB wallet funding instructions ----------------

	setAtoBTab(tab: string) {
		this.atoBTab = tab;
	}

	loadUnmatchedClients() {
		this.loadingUnmatchedClients = true;
		this.conversionService.getWinFactorClientsWithoutAtoBWallet().subscribe(
			(clients) => {
				this.unmatchedClients = clients || [];
				this.loadingUnmatchedClients = false;
			},
			(error) => {
				this.loadingUnmatchedClients = false;
				this.toastrService.error(this.errorText(error), 'AtoB Funding Instructions');
			}
		);
	}

	get filteredUnmatchedClients(): ConversionWinFactorClientName[] {
		const needle = (this.unmatchedFilter || '').trim().toLowerCase();
		if (!needle) {
			return this.unmatchedClients;
		}
		return this.unmatchedClients.filter(
			(x) =>
				(x.companyName || '').toLowerCase().indexOf(needle) >= 0 ||
				(x.factorCloudCompanyName || '').toLowerCase().indexOf(needle) >= 0 ||
				x.winFactorClientId.toString().indexOf(needle) >= 0
		);
	}

	/** Exports the active WinFactor clients with no matching AtoB wallet (as filtered on the tab) to a CSV. */
	exportUnmatchedClients() {
		const clients = this.filteredUnmatchedClients;
		if (!clients || clients.length === 0) {
			return;
		}

		downloadCsv(
			['WinFactor Client ID', 'WinFactor Name', 'FactorCloud Client ID', 'FactorCloud Name'],
			clients.map((x) => [x.winFactorClientId, x.companyName, x.factorCloudClientId, x.factorCloudCompanyName]),
			'Clients_Without_AtoB_Wallet_',
			this.unmatchedFilter ? 'filtered' : 'all'
		);
	}

	loadAtoBWallets() {
		this.loadUnmatchedClients();
		this.loadingAtoBWallets = true;
		const selectedIds = this.atoBWallets.filter((x) => x.selected).map((x) => x.atoBId);
		this.conversionService.getAtoBWalletFunding().subscribe(
			(wallets) => {
				this.atoBWallets = (wallets || []).map((wallet) => {
					wallet.selected = wallet.matchStatus === 'Matched' && selectedIds.indexOf(wallet.atoBId) >= 0;
					return wallet;
				});
				this.loadingAtoBWallets = false;
			},
			(error) => {
				this.loadingAtoBWallets = false;
				this.toastrService.error(this.errorText(error), 'AtoB Funding Instructions');
			}
		);
	}

	get filteredAtoBWallets(): ConversionAtoBWalletFunding[] {
		const needle = (this.atoBFilter || '').trim().toLowerCase();
		return this.atoBWallets.filter(
			(x) =>
				(!this.atoBMatchFilter || x.matchStatus === this.atoBMatchFilter) &&
				(!needle ||
					(x.companyName || '').toLowerCase().indexOf(needle) >= 0 ||
					(x.payTo || '').toLowerCase().indexOf(needle) >= 0 ||
					(x.winFactorClientName || '').toLowerCase().indexOf(needle) >= 0 ||
					(x.factorCloudClientName || '').toLowerCase().indexOf(needle) >= 0 ||
					(x.winFactorClientId !== null && x.winFactorClientId !== undefined && x.winFactorClientId.toString().indexOf(needle) >= 0) ||
					x.atoBId.toString().indexOf(needle) >= 0)
		);
	}

	atoBCount(matchStatus: string): number {
		return this.atoBWallets.filter((x) => x.matchStatus === matchStatus).length;
	}

	/** Only matched wallets without an instruction yet can be sent. */
	canSendAtoB(wallet: ConversionAtoBWalletFunding): boolean {
		return wallet.matchStatus === 'Matched' && !wallet.fundingInstructionSentDate;
	}

	get selectedAtoBWallets(): ConversionAtoBWalletFunding[] {
		return this.atoBWallets.filter((x) => x.selected && this.canSendAtoB(x));
	}

	get allFilteredAtoBSelected(): boolean {
		const sendable = this.filteredAtoBWallets.filter((x) => this.canSendAtoB(x));
		return sendable.length > 0 && sendable.every((x) => x.selected);
	}

	toggleAllAtoB(checked: boolean) {
		this.filteredAtoBWallets.filter((x) => this.canSendAtoB(x)).forEach((x) => (x.selected = checked));
	}

	sendSelectedAtoB() {
		this.startSendingAtoB(this.selectedAtoBWallets.map((x) => x.atoBId));
	}

	sendAllMatchedAtoB() {
		this.startSendingAtoB(this.atoBWallets.filter((x) => this.canSendAtoB(x)).map((x) => x.atoBId));
	}

	/** One wallet per request, so a long batch shows progress and never hits a request timeout. */
	private startSendingAtoB(atoBIds: number[]) {
		if (this.sendingAtoB) {
			return;
		}
		if (atoBIds.length === 0) {
			this.toastrService.info('Nothing to send: no matched wallet is missing its funding instruction.', 'AtoB Funding Instructions');
			return;
		}

		this.sendingAtoB = true;
		this.atoBSendDone = 0;
		this.atoBSendTotal = atoBIds.length;
		this.atoBSendSent = 0;
		this.atoBSendSkipped = 0;
		this.atoBSendFailures = [];

		const next = (index: number) => {
			if (index >= atoBIds.length) {
				this.finishSendingAtoB();
				return;
			}
			this.conversionService.sendAtoBFundingInstructions([atoBIds[index]]).subscribe(
				(results) => {
					(results || []).forEach((result) => this.recordAtoBResult(result));
					this.atoBSendDone++;
					next(index + 1);
				},
				(error) => {
					this.recordAtoBResult({
						atoBId: atoBIds[index],
						payTo: null,
						status: 'Failed',
						factorCloudClientId: null,
						factorCloudFundingInstructionId: null,
						message: this.errorText(error),
					});
					this.atoBSendDone++;
					next(index + 1);
				}
			);
		};
		next(0);
	}

	private recordAtoBResult(result: ConversionAtoBFundingResult) {
		if (result.status === 'Sent' || result.status === 'Adopted') {
			this.atoBSendSent++;
		} else if (result.status === 'AlreadySent') {
			this.atoBSendSkipped++;
		} else {
			this.atoBSendFailures.push(result);
		}

		const shown = this.atoBWallets.filter((x) => x.atoBId === result.atoBId)[0];
		if (shown) {
			shown.sendStatus = result.status;
			shown.sendMessage = result.message;
			shown.selected = false;
		}
	}

	private finishSendingAtoB() {
		this.sendingAtoB = false;
		const failed = this.atoBSendFailures.length;
		const text =
			`${this.atoBSendSent} AtoB funding instruction(s) added` +
			(this.atoBSendSkipped > 0 ? `, ${this.atoBSendSkipped} already there` : '') +
			(failed > 0 ? `, ${failed} failed.` : '.');
		if (failed > 0) {
			this.toastrService.warning(text, 'AtoB Funding Instructions');
		} else {
			this.toastrService.success(text, 'AtoB Funding Instructions');
		}
		this.loadAtoBWallets();
	}

	// ---------------- Send documents to FactorCloud ----------------

	loadDocumentClients() {
		this.loadingDocumentClients = true;
		const selectedIds = this.documentClients.filter((x) => x.selected).map((x) => this.clientKey(x));
		this.conversionService.getDocumentClients(this.customerDocuments).subscribe(
			(clients) => {
				this.documentClients = (clients || []).map((client) => {
					client.selected = selectedIds.indexOf(this.clientKey(client)) >= 0;
					return client;
				});
				this.loadingDocumentClients = false;
				if (this.currentClient) {
					const current = this.documentClients.filter((x) => this.clientKey(x) === this.clientKey(this.currentClient))[0];
					this.currentClient = current || null;
				}
			},
			(error) => {
				this.loadingDocumentClients = false;
				this.toastrService.error(this.errorText(error), this.documentsTitle);
			}
		);
	}

	get filteredDocumentClients(): ConversionDocumentClient[] {
		const needle = (this.clientFilter || '').trim().toLowerCase();
		if (!needle) {
			return this.documentClients;
		}
		return this.documentClients.filter(
			(x) =>
				(x.clientFolderName || '').toLowerCase().indexOf(needle) >= 0 ||
				(x.clientName || '').toLowerCase().indexOf(needle) >= 0 ||
				(x.clientId !== null && x.clientId !== undefined && x.clientId.toString().indexOf(needle) >= 0)
		);
	}

	get selectedDocumentClients(): ConversionDocumentClient[] {
		return this.documentClients.filter((x) => x.selected);
	}

	get allFilteredClientsSelected(): boolean {
		const shown = this.filteredDocumentClients;
		return shown.length > 0 && shown.every((x) => x.selected);
	}

	toggleAllClients(checked: boolean) {
		this.filteredDocumentClients.forEach((x) => (x.selected = checked));
	}

	/** Shows one client's documents below the client list. */
	viewClient(client: ConversionDocumentClient) {
		this.currentClient = client;
		this.loadClientDocuments();
	}

	loadClientDocuments() {
		if (!this.currentClient) {
			this.clientDocuments = [];
			return;
		}
		this.loadingClientDocuments = true;
		this.conversionService.getDocuments(this.currentClient.clientId, this.customerDocuments).subscribe(
			(documents) => {
				this.clientDocuments = documents || [];
				this.loadingClientDocuments = false;
			},
			(error) => {
				this.loadingClientDocuments = false;
				this.toastrService.error(this.errorText(error), this.documentsTitle);
			}
		);
	}

	get unsentClientDocuments(): ConversionDocument[] {
		return this.clientDocuments.filter((x) => !x.sentToFactorCloudDate);
	}

	get selectedClientDocuments(): ConversionDocument[] {
		return this.clientDocuments.filter((x) => x.selected && !x.sentToFactorCloudDate);
	}

	get allUnsentDocumentsSelected(): boolean {
		const unsent = this.unsentClientDocuments;
		return unsent.length > 0 && unsent.every((x) => x.selected);
	}

	toggleAllDocuments(checked: boolean) {
		this.unsentClientDocuments.forEach((x) => (x.selected = checked));
	}

	/** Sends the documents ticked in the current client's list. */
	sendSelectedDocuments() {
		const documents = this.selectedClientDocuments;
		if (documents.length === 0 || this.sending) {
			return;
		}
		if (!this.currentClient || !this.currentClient.factorCloudClientId) {
			this.toastrService.warning(
				this.customerDocuments
					? 'This customer has no FactorCloud debtor yet. Convert a carrier that uses it first.'
					: 'This client is not in FactorCloud yet. Run Convert for the carrier first.',
				this.documentsTitle
			);
			return;
		}
		this.startSending(documents.map((x) => x.documentKey));
	}

	/** Sends every document not yet in FactorCloud for each ticked client. */
	sendSelectedClients() {
		const clients = this.selectedDocumentClients;
		if (clients.length === 0 || this.sending) {
			return;
		}

		const ready = clients.filter((x) => !!x.factorCloudClientId && x.sentCount < x.documentCount);
		const notConverted = clients.filter((x) => !x.factorCloudClientId);
		if (notConverted.length > 0) {
			this.toastrService.warning(
				this.customerDocuments
					? `${notConverted.length} selected customer(s) have no FactorCloud debtor yet and were left out. Convert a carrier that uses them first.`
					: `${notConverted.length} selected client(s) are not in FactorCloud yet and were left out. Run Convert for them first.`,
				this.documentsTitle
			);
		}
		if (ready.length === 0) {
			return;
		}

		// Collect each client's unsent documents first, then send them one at a time.
		this.sending = true;
		this.resetSendCounters(0);
		const keys: number[] = [];
		const collect = (index: number) => {
			if (index >= ready.length) {
				this.sending = false;
				this.startSending(keys);
				return;
			}
			this.conversionService.getDocuments(ready[index].clientId, this.customerDocuments).subscribe(
				(documents) => {
					(documents || []).filter((x) => !x.sentToFactorCloudDate).forEach((x) => keys.push(x.documentKey));
					collect(index + 1);
				},
				(error) => {
					this.toastrService.error(this.errorText(error), this.documentsTitle);
					collect(index + 1);
				}
			);
		};
		collect(0);
	}

	private resetSendCounters(total: number) {
		this.sendDone = 0;
		this.sendTotal = total;
		this.sendSent = 0;
		this.sendSkipped = 0;
		this.sendFailures = [];
	}

	/** One document per request, so a long batch shows progress and never hits a request timeout. */
	private startSending(documentKeys: number[]) {
		if (documentKeys.length === 0) {
			this.toastrService.info('Nothing to send: every document is already in FactorCloud.', this.documentsTitle);
			return;
		}

		this.sending = true;
		this.resetSendCounters(documentKeys.length);

		const next = (index: number) => {
			if (index >= documentKeys.length) {
				this.finishSending();
				return;
			}
			this.conversionService.sendDocuments([documentKeys[index]], this.customerDocuments).subscribe(
				(results) => {
					(results || []).forEach((result) => this.recordSendResult(result));
					this.sendDone++;
					next(index + 1);
				},
				(error) => {
					this.recordSendResult({
						documentKey: documentKeys[index],
						documentFileName: null,
						status: 'Failed',
						factorCloudDocumentId: null,
						message: this.errorText(error),
					});
					this.sendDone++;
					next(index + 1);
				}
			);
		};
		next(0);
	}

	private recordSendResult(result: ConversionDocumentSendResult) {
		if (result.status === 'Sent') {
			this.sendSent++;
		} else if (result.status === 'AlreadySent') {
			this.sendSkipped++;
		} else {
			this.sendFailures.push(result);
		}

		const shown = this.clientDocuments.filter((x) => x.documentKey === result.documentKey)[0];
		if (shown) {
			shown.sendStatus = result.status;
			shown.sendMessage = result.message;
			shown.selected = false;
		}
	}

	private finishSending() {
		this.sending = false;
		const failed = this.sendFailures.length;
		const text = `${this.sendSent} document(s) sent to FactorCloud` + (this.sendSkipped > 0 ? `, ${this.sendSkipped} already there` : '') + (failed > 0 ? `, ${failed} failed.` : '.');
		if (failed > 0) {
			this.toastrService.warning(text, this.documentsTitle);
		} else {
			this.toastrService.success(text, this.documentsTitle);
		}
		this.loadDocumentClients();
		this.loadClientDocuments();
	}

	/** Client (customer) ids can be null (documents outside a folder), so compare on a string key. */
	private clientKey(client: ConversionDocumentClient): string {
		return client.clientId === null || client.clientId === undefined ? 'none:' + (client.clientFolderName || '') : client.clientId.toString();
	}

	private errorText(error: any): string {
		if (error && typeof error.error === 'string' && error.error.length > 0) {
			return error.error;
		}
		return error && error.message ? error.message : 'The request failed.';
	}

	/** First rows of the CSV, for a look before uploading. Handles quoted fields. */
	private buildPreview(csvText: string) {
		const rows: string[][] = [];
		let row: string[] = [];
		let field = '';
		let inQuotes = false;

		for (let i = 0; i < csvText.length; i++) {
			const c = csvText[i];
			if (c === '"') {
				if (inQuotes && csvText[i + 1] === '"') {
					field += '"';
					i++;
				} else {
					inQuotes = !inQuotes;
				}
			} else if (c === ',' && !inQuotes) {
				row.push(field);
				field = '';
			} else if ((c === '\n' || c === '\r') && !inQuotes) {
				if (c === '\r' && csvText[i + 1] === '\n') {
					i++;
				}
				row.push(field);
				field = '';
				if (row.some((x) => x.trim().length > 0)) {
					rows.push(row);
				}
				row = [];
			} else {
				field += c;
			}
		}
		if (field.length > 0 || row.length > 0) {
			row.push(field);
			if (row.some((x) => x.trim().length > 0)) {
				rows.push(row);
			}
		}

		if (rows.length === 0) {
			this.previewColumns = [];
			this.previewRows = [];
			this.previewTotalRows = 0;
			return;
		}

		const header = rows.shift();
		this.previewColumns = header.map((name, index) => ({ name: name.replace(/^\uFEFF/, ''), prop: index.toString() }));
		this.previewTotalRows = rows.length;
		this.previewRows = rows.slice(0, this.previewRowLimit).map((values) => {
			const record = {};
			values.forEach((value, index) => (record[index.toString()] = value));
			return record;
		});
	}
}



// WEBPACK FOOTER //
// ./src/app/portal/conversion/data-upload/conversion-data-upload.component.ts